import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FacebookStyleForm } from '../components/forms/FacebookStyleForm';
import { trackPageView } from '../utils/trackingUtils';
import { PAGE_TITLES } from '../constants/pageTracking';
import logo from '../assets/fulllogo.png';
import { facebookPixelEvents } from '../utils/facebookPixel';
import { usePageTracking } from '../hooks/usePageTracking';

export default function FacebookLandingPage() {
  usePageTracking(); // Add page tracking

  // Track page view when component mounts
  useEffect(() => {
    // Track regular page view
    trackPageView('FB_LANDING', 'facebook-landing');

    // Track Facebook Pixel page view
    facebookPixelEvents.viewContent({
      content_name: PAGE_TITLES.FB_LANDING.tracking,
      content_category: 'landing_page',
      content_type: 'facebook_ad'
    });
  }, []);

  // Track form visibility with Facebook Pixel
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Google Analytics tracking
            if (typeof window.gtag !== 'undefined') {
              window.gtag('event', 'form_view', {
                page_title: PAGE_TITLES.FB_LANDING.tracking,
                form_type: 'facebook_landing'
              });
            }

            // Facebook Pixel form view tracking
            facebookPixelEvents.track('ViewContent', {
              content_name: 'Facebook Landing Form',
              content_category: 'form',
              content_type: 'facebook_landing'
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    const formElement = document.getElementById('facebook-landing-form');
    if (formElement) observer.observe(formElement);

    return () => observer.disconnect();
  }, []);

  // Structured data for the landing page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "EmploSkills - Virtual Business Challenge Registration",
    "description": "Register for the Virtual Business Challenge - A unique opportunity for homeschool students to develop real-world business skills through simulation.",
    "url": "https://homeschool.emploskills.com/fb-landing",
    "logo": logo,
    "potentialAction": {
      "@type": "RegisterAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://homeschool.emploskills.com/fb-landing",
        "actionPlatform": [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform"
        ]
      },
      "result": {
        "@type": "Registration",
        "name": "Virtual Business Challenge Registration"
      }
    }
  };

  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "EmploSkills",
    "url": "https://homeschool.emploskills.com",
    "logo": "https://cdn.prod.website-files.com/648d80e4bdcb44458774633e/6490adaa3b8aff0c3c278948_fulllogo-p-500.png",
    "sameAs": [
      "https://www.linkedin.com/company/19208371/",
      "https://www.facebook.com/Emploskills/"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "contact",
      "email": "contact@emploskills.com"
    }
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLES.FB_LANDING?.seo || 'Join the Virtual Business Challenge - EmploSkills'}</title>
        <meta 
          name="description" 
          content="Register now for the Virtual Business Challenge. Perfect for homeschool students aged 14-18. Develop real-world business skills through our innovative simulation platform." 
        />
        <meta 
          name="keywords" 
          content="business simulation, homeschool business education, virtual business challenge, student competition, business skills" 
        />
        
        {/* Open Graph / Social Media */}
        <meta property="og:title" content="Join the Virtual Business Challenge - EmploSkills" />
        <meta 
          property="og:description" 
          content="Register for our Virtual Business Challenge and give your homeschool teen the opportunity to develop real-world business skills." 
        />
        <meta property="og:image" content={logo} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://homeschool.emploskills.com/fb-landing" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Join the Virtual Business Challenge - EmploSkills" />
        <meta 
          name="twitter:description" 
          content="Register for our Virtual Business Challenge and give your homeschool teen the opportunity to develop real-world business skills." 
        />
        <meta name="twitter:image" content={logo} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationStructuredData)}
        </script>
      </Helmet>

      <div 
        id="facebook-landing-form" 
        className="min-h-screen bg-[#f0f2f5] flex items-center justify-center py-8 px-4"
      >
        <FacebookStyleForm />
      </div>
    </>
  );
} 